import React, { useMemo } from "react";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import toast from "react-hot-toast";
import {
  useDeleteTeacherRate,
  useTeacherRates,
} from "../hooks/useTeacherRates";

function formatCOP(value) {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(value);
}

function TeacherRateList({ onEdit }) {
  const { data: rates, isLoading, isError } = useTeacherRates();
  const deleteMutation = useDeleteTeacherRate();

  const handleDelete = (rate) => {
    if (!window.confirm("¿Eliminar esta tarifa?")) return;
    deleteMutation.mutate(rate.id, {
      onSuccess: () => toast.success("Tarifa eliminada"),
      onError: () => toast.error("Error al eliminar la tarifa"),
    });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "teacher_display",
        header: "Docente",
        cell: ({ row }) =>
          row.original.teacher_display ?? row.original.teacher ?? "—",
      },
      {
        accessorKey: "profile_type_display",
        header: "Tipo de perfil",
        cell: ({ row }) =>
          row.original.profile_type_display ?? row.original.profile_type ?? "—",
      },
      {
        accessorKey: "hourly_value",
        header: "Valor/hora",
        cell: ({ getValue }) => formatCOP(getValue()),
      },
      {
        accessorKey: "effective_from",
        header: "Vigente desde",
      },
      {
        accessorKey: "is_active",
        header: "Activo",
        cell: ({ getValue }) => (
          <Badge bg={getValue() ? "success" : "secondary"}>
            {getValue() ? "Sí" : "No"}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "Acciones",
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              style={{ minHeight: 44 }}
              onClick={() => onEdit(row.original)}
            >
              Editar
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              style={{ minHeight: 44 }}
              onClick={() => handleDelete(row.original)}
              disabled={deleteMutation.isPending}
            >
              Eliminar
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation.isPending]
  );

  // Sort by teacher then by effective_from descending to show history
  const sortedRates = useMemo(() => {
    if (!rates) return [];
    return [...rates].sort((a, b) => {
      const ta = String(a.teacher ?? "");
      const tb = String(b.teacher ?? "");
      if (ta !== tb) return ta.localeCompare(tb);
      return String(b.effective_from ?? "").localeCompare(
        String(a.effective_from ?? "")
      );
    });
  }, [rates]);

  const table = useReactTable({
    data: sortedRates,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading)
    return <Spinner animation="border" role="status" aria-label="Cargando tarifas" />;
  if (isError)
    return <Alert variant="danger">Error al cargar las tarifas.</Alert>;

  const rows = table.getRowModel().rows;

  return (
    <>
      {/* Desktop */}
      <div className="table-responsive d-none d-sm-block">
        <Table striped bordered hover className="mb-0">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  Sin tarifas registradas
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Mobile: tarjetas agrupadas por docente */}
      <div className="d-sm-none">
        {sortedRates.length === 0 && (
          <p className="text-muted text-center py-3">Sin tarifas registradas</p>
        )}
        {sortedRates.map((rate) => (
          <div key={rate.id} className="card mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h6 className="card-title mb-0">
                  {rate.teacher_display ?? rate.teacher ?? "Docente"}
                </h6>
                <Badge bg={rate.is_active ? "success" : "secondary"}>
                  {rate.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
              <p className="mb-1">
                <strong>Tipo:</strong>{" "}
                {rate.profile_type_display ?? rate.profile_type ?? "—"}
              </p>
              <p className="mb-1">
                <strong>Valor/hora:</strong> {formatCOP(rate.hourly_value)}
              </p>
              <p className="mb-3">
                <strong>Vigente desde:</strong> {rate.effective_from ?? "—"}
              </p>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  style={{ minHeight: 44, flex: 1 }}
                  onClick={() => onEdit(rate)}
                >
                  Editar
                </Button>
                <Button
                  variant="outline-danger"
                  style={{ minHeight: 44, flex: 1 }}
                  onClick={() => handleDelete(rate)}
                  disabled={deleteMutation.isPending}
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export { TeacherRateList };
